import { Drawer, Form } from 'antd';
import Box from 'components/Box';
import appointmentActions from 'features/appointment/services/actions';
import appointmentApis from 'features/appointment/services/apis';
import { APPOINTMENT_STATUS } from 'features/appointment/services/constants';
import { aptUIActions } from 'features/appointment/services/reducers';
import { IBodyApiBookingMultiple, IBodyApiBookingMultipleItem, IBodyApiUpdateAppointmentV2 } from 'features/appointment/services/types/api';
import { AptDetailV3Data, IAppointmentItemData } from 'features/appointment/services/types/appointment';
import useTabletApp from 'hooks/useTabletApp';
import _, { delay, first, get, padEnd, set, sortBy } from 'lodash';
import moment, { Moment } from 'moment';
import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IBodyApiUpdateStatusApt, IResponseDataBody } from 'services/response';
import shopSelectors from 'services/shop/selectors';
import { IStaffItemData } from 'services/shop/types/staff';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { useAppDispatch } from 'store/hooks';
import storage from 'utils/sessionStorage';
import { momentTimezone } from 'utils/time';
import uuid from 'utils/uuid';
import RequestAdminPWCallback, { useRequestAdminPWCallbackRef } from 'widgets/RequestAdminLocalPW/Callback';
import ModalInputReason, { useModalInputReasonRef } from '../AppointmentDetail/v2/ModalReason';
import { useStaffListChooseUI } from '../QuickBookingV2/Staff';
import { useTimeSelection } from '../QuickBookingV2/TimeDate';
import { IQuickBookingV2CustomerItem, IQuickBookingV2FormValues, IQuickBookingV2ServiceItem } from '../QuickBookingV2/type';
import AppointmentDetailV3UI, { AppointmentDetailV3UIProps } from './DetailUI';
import AppointmentDetailV3Footer from './Footer';
import AppointmentDetailV3Header from './Header';
import toast from 'utils/toast';
import ModalDurations, { useModalDurationsRef } from './ModalDurations';
import ModalDeleteSubAppointment, { useModalDeleteSubAppointment } from './ModalDeleteSubAppointment';
import ModalConfirmSubAppointment, { useModalConfirmSubAppointment } from './ModalConfirmSubAppointment';
import ModalWarning from 'widgets/ModalWarning';
type Props = {
  v2?: boolean;
};
type Ref = {
  open: (id: string, customerId?: string) => void;
};
const bodyStyle = { padding: 0 };
export const aptDetailV3Ref = React.createRef<Ref>();
const AppointmentDetailV3 = forwardRef<Ref, Props>(({ }, ref) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const tabletApp = useTabletApp();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<AptDetailV3Data | null>(null);
  const onClose = () => setOpen(false);
  const setLoading = useSetLoadingPage();
  const [form] = Form.useForm();
  const timeRef = useTimeSelection();
  const durationRef = useModalDurationsRef();
  const staffRef = useStaffListChooseUI();
  const pinCodeRef = useRequestAdminPWCallbackRef();
  const reasonRef = useModalInputReasonRef();
  const staffs = shopSelectors.data.staffs();
  const modalDeleteSubAppointmentRef = useModalDeleteSubAppointment();
  const modalConfirmSubAppointmentRef = useModalConfirmSubAppointment();

  const onPickDate = async (date: Moment) => {
    form.setFieldValue('date', date);
    setTimeout(() => {
      if (date.format('MM-DD-YYYY') === moment().format('MM-DD-YYYY')) {
        staffRef.current?.fetchNextAvailable();
      }
      timeRef.current?.fetch(date);
    }, 100);

    form.setFieldValue('loadingTimeSlot', true);
    const defaultStartTime = form.getFieldValue('defaultStartTime');
    try {
      const res: IResponseDataBody<string[]> = await appointmentApis.getAvailableTimeSlotStore(date.format('MM-DD-YYYY'));
      if (res.data.data) {
        const timeSlots = res.data.data.map((o) => moment(o, 'HH:mm').format('hh:mm A'));
        form.setFieldValue('timeSlots', timeSlots);
      }
    } catch (error) {
      form.setFieldValue('timeSlots', []);
    } finally {
      form.setFieldValue('loadingTimeSlot', false);
    }
  };

  const getListStaff = (serviceId: string, servicesProps?: IQuickBookingV2ServiceItem[] | undefined) => delay(async () => {
    const values: IQuickBookingV2FormValues = form.getFieldsValue();
    const servicesList = servicesProps || values.services;
    const service = servicesList.find((o) => o.serviceId === serviceId);
    if (!service) return;
    const startTime = service?.masterStartTime
      ? service?.masterStartTime
      : service?.startTime;
    const duration =
      service?.masterDuration !== undefined
        ? service?.masterDuration
        : service?.duration || 0;
    const startTimeMoment = moment(
      values.date.format('MM-DD-YYYY') + ' ' + startTime || '',
      'MM-DD-YYYY hh:mm A'
    );
    const endTimeMoment = startTimeMoment.clone().add(duration, 'minutes');
    const body = {
      serviceIds: [serviceId],
      startTime: startTimeMoment.format('MM-DD-YYYY HH:mm'),
      endTime: endTimeMoment.format('MM-DD-YYYY HH:mm'),
      type: 'POS',
    };
    try {
      staffRef.current?.setLoading(true);
      const res: IResponseDataBody<IStaffItemData[]> =
        await appointmentApis.getListStaffByTime(body);
      if (res.data.data) {
        form.setFieldValue('staffs', res.data.data);
      } else {
        form.setFieldValue('staffs', []);
      }
    } catch (error) {
      form.setFieldValue('staffs', []);
    } finally {
      staffRef.current?.setLoading(false);
    }
  }, 100);

  const onChooseNumCustomer = (value: number) => {
    const customerList: IQuickBookingV2CustomerItem[] = form.getFieldValue('customerList');
    if (!customerList.length) {
      form.setFieldValue('customerList', Array.from({ length: value }, (item, index) => {
        return ({
          id: uuid(),
          name: '',
          customer: null,
          services: [],
        } as IQuickBookingV2CustomerItem);
      }));
      return;
    }

    const cloneCustomerList = [...customerList];

    if (customerList.length > value) {
      form.setFieldValue('customerList', cloneCustomerList.slice(0, value));
      return;
    }

    const length = padEnd(customerList.map(() => 'x').join(''), value, '*').replaceAll('x', '').length;
    Array.from({ length }, (item, index) => {
      const result = ({
        id: uuid(),
        name: '',
        services: [],
      } as IQuickBookingV2CustomerItem);
      cloneCustomerList.push(result);
    });
    form.setFieldValue('customerList', cloneCustomerList);
  };

  const onPickStaff = (item: IStaffItemData) => {
    const activeServiceId: string = form.getFieldValue('activeServiceId');

    const customerList: IQuickBookingV2FormValues['customerList'] = form.getFieldValue('customerList');
    const newList = customerList.map(o => {
      if (activeServiceId?.includes(o.id)) {
        return ({
          ...o,
          services: o.services.map(s => {
            if (s.id === activeServiceId) {
              return ({
                ...s,
                staffId: item.id,
                staffName: item.firstName,
                staffAvatar: item.urlImage,
              });
            }
            return s;
          })
        });
      }
      return o;
    });
    form.setFieldValue('customerList', newList);
  };

  const updateStatus = async (status: APPOINTMENT_STATUS, reason?: string) => {
    const appointmentId = detail?.appointmentId;
    if (!appointmentId) return;

    setLoading(true);
    const body: IBodyApiUpdateStatusApt = {
      appointmentId,
      reason: reason || '',
      status,
    };
    try {
      const res: IResponseDataBody<boolean> = await appointmentApis.updateStatusApt(body);
      if (res.data.data) {
        dispatch(appointmentActions.updateCurrentAppointment({ appointmentId, data: { status: body.status, reason: body.reason } }));
        onClose();
      }
    } catch (error) { } finally {
      setLoading(false);
    }
  };

  const onNoShow = async () => {
    if (!detail) return;
    const body = await modalDeleteSubAppointmentRef.current?.open(detail, APPOINTMENT_STATUS.NO_SHOW);
    if (!body?.appointmentIds?.length) return;
    setLoading(true);
    try {
      const res: IResponseDataBody<IAppointmentItemData[]> = await appointmentApis.updateStatusMultipleAppointment(body);
      if (res.data.data) {
        dispatch(aptUIActions.syncAppointments(res.data.data));
        onClose();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onDecline = async () => {
    if (!detail) return;
    const body = await modalDeleteSubAppointmentRef.current?.open(detail, APPOINTMENT_STATUS.CANCEL);
    if (!body?.appointmentIds?.length) return;
    setLoading(true);
    try {
      const res: IResponseDataBody<IAppointmentItemData[]> = await appointmentApis.updateStatusMultipleAppointment(body);
      if (res.data.data) {
        dispatch(aptUIActions.syncAppointments(res.data.data));
        onClose();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    if (!detail) return;
    const body = await modalConfirmSubAppointmentRef.current?.open(detail, { status: APPOINTMENT_STATUS.CONFIRM, note: form.getFieldValue('note') });
    if (!body?.appointmentIds?.length) return;
    setLoading(true);
    try {
      const res: IResponseDataBody<IAppointmentItemData[]> = await appointmentApis.updateStatusMultipleAppointment(body);
      if (res.data.data) {
        dispatch(aptUIActions.syncAppointments(res.data.data));
        onClose();
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onUpdate = () => form.submit();

  const onDelete = () => pinCodeRef.current?.open(async () => {
    if (!detail) return;
    const body = await modalDeleteSubAppointmentRef.current?.open(detail, APPOINTMENT_STATUS.DELETE);
    if (!body?.appointmentIds?.length) return;
    setLoading(true);
    try {
      const res: IResponseDataBody<IAppointmentItemData[]> = await appointmentApis.updateStatusMultipleAppointment(body);
      if (res.data.data) {
        dispatch(aptUIActions.syncAppointments(res.data.data));
        onClose();
      }
    } catch (error) { } finally {
      setLoading(false);
    }
  });

  const onCheckout = async () => {
    const appointmentId = detail?.appointmentId;
    if (!appointmentId) return;
    setLoading(true);
    try {
      const res: IResponseDataBody<string> = await appointmentApis.checkoutAppointment(appointmentId);
      const billId = res.data.data;
      if (billId) {
        if (tabletApp.enable) {
          tabletApp.cashierTicket(billId);
        } else {
          navigate(`/store/${storage.shop_id.get()}/ticket/edit/${billId}`);
        }
        onClose();
      }
    } catch (error) { } finally {
      setLoading(false);
    }
  };

  const handleUpdateApt: AppointmentDetailV3UIProps['handleFinish'] = async (formValues) => {
    const appointmentId = detail?.appointmentId || '';
    if (!appointmentId) return;


    const body: IBodyApiUpdateAppointmentV2 = {
      appointmentId,
      createdBy: '',
      note: formValues.note,
      customerId: formValues.customer?.id || '',
      numberCustomers: formValues.numCustomers,
    };

    const newKey = JSON.stringify(formValues.customerList.map(o => [o.id, o.name, o.services.map(s => [s.serviceId, s.staffId, s.masterStartTime || s.startTime, s.masterDuration || s.duration].join(',')).join(',')].join(',')).join(','));
    if (newKey !== formValues.initialServiceKey) {
      const items: IBodyApiBookingMultiple['items'] = [];
      formValues.customerList.map((c, cIndex) => c.services.map(
        (o) => {
          const startTime =
            moment(formValues.date).format('MM/DD/YYYY') +
            ' ' +
            moment(o.masterStartTime || o.startTime, 'hh:mm A').format('HH:mm');
          const duration = o.masterDuration ? o.masterDuration : o.duration || 0;
          const result: IBodyApiBookingMultipleItem = {
            staffId: o.staffId,
            serviceId: o.serviceId,
            requestStaff: o.aptStatus === 'request',
            startTime,
            endTime: moment(startTime, 'MM/DD/YYYY HH:mm')
              .add(duration, 'minutes')
              .format('MM/DD/YYYY HH:mm'),
            customerName: c.customer?.name || c.name,
            tempCustomerId: c.customer?.id || c.id,
            detailAppointmentId: o.detailAppointmentId,
          };
          items.push(result);
        }
      ));
      set(body, 'items', items);
    }

    if (formValues.repeat) {
      set(body, 'repeat', formValues.repeat);
    }

    setLoading(true);
    try {
      const res: IResponseDataBody<IAppointmentItemData[]> =
        await appointmentApis.updateBookingV2(body);
      if (res.data.data) {
        dispatch(aptUIActions.syncAppointments(res.data.data));
        onClose();
      }
    } catch (error) {
      const message = get(error, 'response.data.message') || '';
      ModalWarning.open({ message });
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailApt = async (appointmentId: string, customerId?: string) => {
    try {
      const res: IResponseDataBody<AptDetailV3Data> = await appointmentApis.getAppointmentDetailV2(appointmentId);
      const resData = res.data.data;
      if (resData) {
        setDetail(resData);
        setOpen(true);
        const date = moment(resData.startTime, 'MM-DD-YYYY hh:mm:ss');
        const groupByData = _.chain(resData.numberCustomers > 1 ? resData.items : resData.items.map(o => {
          return {
            ...o,
            tempCustomerId: resData.customerId,
          };
        }))
          .groupBy('tempCustomerId');

        const customerListRaw: IQuickBookingV2CustomerItem[] = groupByData.map((value) => {
          const firstItem = _.first(value);
          const id = firstItem?.tempCustomerId || '';
          return ({
            id: uuid(),
            name: firstItem?.customerName || '',
            customer: {
              id,
              name: firstItem?.customerName || '',
              phone: firstItem?.customerPhone || '',
            },
            services: value.map(s => {
              const startTime = moment(s.startTime, 'hh:mm A');
              const endTime = moment(s.endTime, 'hh:mm A');
              const result: IQuickBookingV2ServiceItem = ({
                id: `${id}_${uuid()}`,
                staffId: s.staffId,
                aptStatus: s.requestStaff ? 'request' : 'none-request',
                duration: s.duration || endTime.diff(startTime, 'minutes'),
                serviceId: s.serviceId,
                serviceName: s.serviceName,
                staffAvatar: s.staffAvatar,
                staffName: s.staffName,
                startTime: startTime.format('hh:mm A'),
                detailAppointmentId: s.detailAppointmentId,
              });
              return result;
            })
          });
        })
          .value();

        const customerList: IQuickBookingV2CustomerItem[] = sortBy(customerListRaw, o => o.customer.id !== resData.customerId);

        setTimeout(() => onPickDate(date), 100);

        const values = {
          activeServiceId: '',
          date,
          duration: resData.duration,
          note: resData.note,
          numCustomers: resData.numberCustomers || 1,
          repeat: 0,
          staffs: [],
          startTime: date.format('hh:mm A'),
          customerList,
          services: [],
          sourceType: resData.sourceType,
          customer: {
            id: resData.customerId,
            name: resData.customerName,
            phone: resData.customerPhone,
          },
          masterCustomerId: resData.customerId,
        };

        const initialServiceKey = JSON.stringify(customerList.map(o => [o.id, o.name, o.services.map(s => [s.serviceId, s.staffId, s.duration, s.startTime, s.duration].join(',')).join(',')].join(',')).join(','));
        form.setFieldsValue(values);
        form.setFieldValue('initialServiceKey', initialServiceKey);
      }
    } catch (error) { } finally {
      setLoading(false);
    }
  };

  const handleChanceDuration = async (_initialDuration: number) => {
    const duration = await durationRef.current?.open(_initialDuration);
    if (!duration) return;
    if (!detail?.appointmentId) return;
    form.setFieldValue('duration', duration);
    setLoading(true);
    try {
      const res: IResponseDataBody<IAppointmentItemData> = await appointmentApis.updateDurationAppointment({ appointmentId: detail?.appointmentId || '', duration });
      if (res.data.data) {
        set(res.data.data, 'action', 'UPDATE');
        dispatch(aptUIActions.syncAppointments([res.data.data]));
        onClose();
      }
    } catch (error) { } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: async (appointmentId) => {
      form.resetFields();
      setLoading(true);
      try {
        await fetchDetailApt(appointmentId);
      } catch (error) { } finally {
        setLoading(false);
      }
    },
  }));

  return (
    <Drawer
      onClose={onClose} open={open}
      title={<></>}
      headerStyle={{ display: 'none' }}
      width={'auto'}
      bodyStyle={bodyStyle}
    >
      <Form
        onFinish={handleUpdateApt as any}
        form={form}
        initialValues={{
          date: moment(),
          duration: undefined,
          staffs: undefined,
          numCustomers: 1,
          customerList: [],
          sameTimeBooking: false,
        }}
        onFinishFailed={(errorInfo: any) => {
          const error: string = get(first(errorInfo.errorFields), ['errors', 0], '') || '';
          if (!error) return;
          toast.error(error);
        }}
        style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
      >
        <AppointmentDetailV3Header status={detail?.status} />
        <AppointmentDetailV3UI
          form={form}
          onPickDate={onPickDate}
          getListStaff={getListStaff}
          onChooseNumCustomer={onChooseNumCustomer}
          onPickStaff={onPickStaff}
          staffRef={staffRef}
          timeRef={timeRef}
          staffs={staffs}
          handleFinish={handleUpdateApt}
          onChanceDuration={handleChanceDuration}
        />
        <Form.Item name={'initialServiceKey'} noStyle />
        <Form.Item shouldUpdate noStyle>{({ getFieldValue }) => {
          const status = detail?.status || 0;
          const customerList = getFieldValue('customerList');
          const appointmentDate = momentTimezone(detail?.startTime);
          const now = moment();
          const isSameOrBefore = appointmentDate.isSameOrBefore(now, 'date');
          return <AppointmentDetailV3Footer
            status={status}
            onClose={onClose}
            onDelete={onDelete}
            onNoShow={onNoShow}
            onDecline={onDecline}
            onConfirm={onConfirm}
            onUpdate={onUpdate}
            onCheckout={onCheckout}
            disabledUpdate={!customerList?.length}
            visibleCheckout={isSameOrBefore}
          />;
        }}</Form.Item>
      </Form>
      <ModalInputReason v2 ref={reasonRef} />
      <ModalDurations ref={durationRef} />
      <ModalDeleteSubAppointment ref={modalDeleteSubAppointmentRef} />
      <ModalConfirmSubAppointment ref={modalConfirmSubAppointmentRef} />
      <RequestAdminPWCallback onlyAdmin v2 ref={pinCodeRef} />
    </Drawer>
  );
});
AppointmentDetailV3.displayName = 'AppointmentDetailV3';
export default AppointmentDetailV3;

